import React, { useCallback, useEffect, useState } from 'react';
import { healthsherpa } from './api';
import { rowTone } from './hsColors';
import { displayName } from './hsLabels';
import { REL_LABEL, fmtDob, genderLabel, tobaccoLabel } from './hsPersonal';
import { fs } from './hsScale';

// =============================================================================
// Personal info for quoting — 5.46.0, reworked 5.48.0 (Henry 2026-11-02: HealthSherpa's export masks DOB and income,
// so the values come from staff work and are kept for later years).
//   Per member: date of birth + gender (permanent), tobacco (per plan year). Per household + plan year: income, size.
//   A plan year without tobacco / income / size shows LAST year's as "last year: …" (masked like the rest).
//   Everything is MASKED until "Show" (logged: user, person, time — admins see the log in HealthSherpa → Settings).
//   "Edit personal info" (all staff): add / correct every member's DOB / gender / tobacco and the year's income / size
//   (opening it is a logged Show; blank = unchanged). Each value shows where it came from (export / enrollment / quote /
//   staff), who and when. Values live only in this component's state while shown — never in the address bar, storage or logs.
// =============================================================================

const colors = (darkMode) => ({
  panel: rowTone('panel', darkMode), border: darkMode ? '#3a3f46' : '#d5d9df', text: rowTone('text', darkMode),
  sub: rowTone('subtext', darkMode),
});
const detailOf = (e) => e?.response?.data?.detail || e?.message || 'Something went wrong';
const MASK = '••••••';
const SOURCE_WORD = { export: 'HealthSherpa export', enrollment: 'Start enrollment', quote: 'a quote', staff: 'typed by staff' };
const money = (v) => `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const btn = (c, primary) => ({
  padding: '3px 10px', fontSize: fs(12), borderRadius: 5, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: primary ? 600 : 400,
  border: primary ? '1px solid #185FA5' : `1px solid ${c.border}`, background: primary ? '#185FA5' : c.panel, color: primary ? '#fff' : c.text,
});
const inputStyle = (c) => ({ padding: '3px 6px', fontSize: fs(12), borderRadius: 4, border: `1px solid ${c.border}`, background: c.panel, color: c.text });

const name = (m) => [m.first, m.last].filter(Boolean).map((x) => String(x).split(/\s+/).map(displayName).join(' ')).join(' ')
  || REL_LABEL[m.relationship] || m.relationship;
const provText = (p) => (p ? `${SOURCE_WORD[p.source] || p.source}${p.by ? ` · ${p.by}` : ''}${p.at_label ? ` · ${p.at_label}` : ''}` : '');

// "Edit personal info": starts from the shown values; only changed fields are sent (blank = unchanged).
function EditForm({ c, personId, year, shown, members, onSaved, onCancel }) {
  const start = {};
  members.forEach((m) => {
    const v = (shown.members || []).find((x) => x.member_key === m.member_key) || {};
    start[m.member_key] = { dob: v.dob || '', gender: v.gender || '', tobacco: v.tobacco === true ? 'yes' : v.tobacco === false ? 'no' : '' };
  });
  const hh0 = { income: (shown.household && shown.household.income && shown.household.income.value) || '',
    size: (shown.household && shown.household.size && shown.household.size.value != null) ? String(shown.household.size.value) : '' };
  const [vals, setVals] = useState(start);
  const [hh, setHh] = useState(hh0);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, f, v) => setVals((x) => ({ ...x, [k]: { ...x[k], [f]: v } }));
  const save = async () => {
    setBusy(true); setError(null);
    try {
      const out = [];
      members.forEach((m) => {
        const a = vals[m.member_key]; const b = start[m.member_key]; const row = { member_key: m.member_key };
        if (a.dob && a.dob !== b.dob) row.dob = a.dob;
        if (a.gender && a.gender !== b.gender) row.gender = a.gender;
        if (a.tobacco && a.tobacco !== b.tobacco) row.tobacco = a.tobacco === 'yes';
        if (Object.keys(row).length > 1) out.push(row);
      });
      const house = {};
      if (hh.income !== '' && hh.income !== hh0.income) house.income = String(hh.income);
      if (hh.size !== '' && hh.size !== hh0.size) house.size = String(hh.size);
      const r = await healthsherpa.coverage.savePersonal(personId, { plan_year: year, members: out, household: Object.keys(house).length ? house : null });
      onSaved(r.data);
    } catch (e) { setError(detailOf(e)); }
    setBusy(false);
  };
  const last = (f) => shown.household && shown.household[f] && shown.household[f].last_year;
  return (
    <div style={{ border: `1px solid ${c.border}`, borderRadius: 6, padding: 8, margin: '6px 0' }} data-testid="pi-edit-form">
      <div style={{ fontSize: fs(12), color: c.sub, marginBottom: 6 }}>
        Date of birth and gender are kept for every year; tobacco, income and household size are for {year}. Blank = unchanged.
      </div>
      {members.map((m) => (
        <div key={m.member_key} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', padding: '2px 0' }} data-testid="pi-edit-member">
          <span style={{ minWidth: 150 }}>{name(m)} <span style={{ color: c.sub }}>· {REL_LABEL[m.relationship] || m.relationship}</span></span>
          <input type="date" aria-label="Date of birth" value={vals[m.member_key].dob} onChange={(e) => set(m.member_key, 'dob', e.target.value)} style={inputStyle(c)} data-testid="pi-edit-dob" />
          <select aria-label="Gender" value={vals[m.member_key].gender} onChange={(e) => set(m.member_key, 'gender', e.target.value)} style={inputStyle(c)} data-testid="pi-edit-gender">
            <option value="">Gender…</option><option value="female">Female</option><option value="male">Male</option>
          </select>
          <select aria-label={`Tobacco ${year}`} value={vals[m.member_key].tobacco} onChange={(e) => set(m.member_key, 'tobacco', e.target.value)} style={inputStyle(c)} data-testid="pi-edit-tobacco">
            <option value="">Tobacco {year}…</option><option value="no">No tobacco</option><option value="yes">Tobacco user</option>
          </select>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
        <span style={{ minWidth: 150 }}>Household {year}</span>
        <input type="number" min="0" placeholder={last('income') ? `last year: ${money(last('income').value)}` : 'Annual income'} aria-label={`Household income ${year}`}
          value={hh.income} onChange={(e) => setHh({ ...hh, income: e.target.value })} style={{ ...inputStyle(c), width: 160 }} data-testid="pi-edit-income" />
        <input type="number" min="1" max="30" placeholder={last('size') ? `last year: ${last('size').value}` : 'Size'} aria-label={`Household size ${year}`}
          value={hh.size} onChange={(e) => setHh({ ...hh, size: e.target.value })} style={{ ...inputStyle(c), width: 110 }} data-testid="pi-edit-size" />
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button style={btn(c, true)} disabled={busy} onClick={save} data-testid="pi-edit-save">{busy ? 'Saving…' : 'Save'}</button>
        <button style={btn(c)} onClick={onCancel}>Cancel</button>
        {error && <span role="alert" style={{ fontSize: fs(12), color: '#c0392b' }}>{error}</span>}
      </div>
    </div>
  );
}

export default function PersonalInfo({ personId, planYear, darkMode, testid = 'pi', onlyCovered = false, compact = false }) {
  const c = colors(darkMode);
  const [sum, setSum] = useState(null);
  const [shown, setShown] = useState(null);       // the revealed values (state only)
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    healthsherpa.coverage.personal(personId, planYear).then((r) => setSum(r.data)).catch((e) => setError(detailOf(e)));
  }, [personId, planYear]);
  useEffect(() => { setSum(null); setShown(null); setEditing(false); setError(null); load(); }, [load]);

  const year = (sum && sum.plan_year) || planYear || new Date().getFullYear();
  const show = async () => {
    setBusy(true); setError(null);
    try { const r = await healthsherpa.coverage.showPersonal(personId, year); setShown(r.data); return r.data; } catch (e) { setError(detailOf(e)); return null; }
    finally { setBusy(false); }
  };
  const edit = async () => { if (shown || await show()) setEditing(true); };
  if (!sum) return <div style={{ fontSize: fs(13), color: c.sub }} data-testid={testid}>{error || 'Loading…'}</div>;
  const members = sum.members.filter((m) => !onlyCovered || m.relationship === 'primary' || m.applying !== false);
  const byKey = {};
  ((shown && shown.members) || []).forEach((m) => { byKey[m.member_key] = m; });
  const hhShown = shown && shown.household;
  const hhCell = (f, fmt) => {
    const st = sum.household && sum.household[f];
    if (!st || !st.state) return <span style={{ color: c.sub }}>—</span>;
    if (st.state === 'last_year') {
      const lv = hhShown && hhShown[f] && hhShown[f].last_year;
      return <span style={{ color: c.sub }} data-testid={`${testid}-${f}-last`}>last year ({st.year}): {lv ? fmt(lv.value) : MASK}</span>;
    }
    const v = hhShown && hhShown[f];
    return <span title={v ? provText(v.prov) : ''}>{v ? fmt(v.value) : MASK}</span>;
  };
  return (
    <div data-testid={testid} style={{ fontSize: fs(13) }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        {shown
          ? <button style={btn(c)} onClick={() => { setShown(null); setEditing(false); }} data-testid={`${testid}-hide`}>Hide</button>
          : <button style={btn(c, true)} disabled={busy} onClick={show} data-testid={`${testid}-show`}
            title="Shows dates of birth, gender, tobacco, income and household size — each Show is logged">{busy ? '…' : 'Show personal info'}</button>}
        {!editing && <button style={btn(c)} disabled={busy} onClick={edit} data-testid={`${testid}-edit`} title="Opening the editor shows the values (logged)">Edit personal info</button>}
        {sum.missing_dob > 0 && <span style={{ fontSize: fs(12), color: '#b9770e' }} data-testid={`${testid}-missing`}>{sum.missing_dob} date{sum.missing_dob === 1 ? '' : 's'} of birth missing</span>}
        {!shown && <span style={{ fontSize: fs(11), color: c.sub }}>Masked · each Show is logged</span>}
      </div>
      {editing && shown && (
        <EditForm c={c} personId={personId} year={year} shown={shown} members={members}
          onCancel={() => setEditing(false)} onSaved={(s) => { setSum(s); setEditing(false); setShown(null); }} />
      )}
      {members.length === 0 && <div style={{ color: c.sub }}>—</div>}
      {members.map((m) => {
        const v = byKey[m.member_key];
        const tLast = m.last_year && m.last_year.tobacco;
        return (
          <div key={m.member_key} data-testid={`${testid}-member`} data-rel={m.relationship}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline', padding: compact ? '1px 0' : '3px 0' }}>
              <span style={{ minWidth: 150 }}>{name(m)} <span style={{ color: c.sub }}>· {REL_LABEL[m.relationship] || m.relationship}</span></span>
              <span data-testid={`${testid}-dob`} title={v ? provText(v.prov && v.prov.dob) : ''}>DOB {m.has.dob ? (v ? `${fmtDob(v.dob)} · age ${v.age_jan1} on Jan 1 ${year}` : MASK) : <span style={{ color: '#b9770e' }}>missing</span>}</span>
              <span data-testid={`${testid}-gender`} title={v ? provText(v.prov && v.prov.gender) : ''}>{m.has.gender ? (v ? genderLabel(v.gender) : `Gender ${MASK}`) : <span style={{ color: c.sub }}>gender —</span>}</span>
              <span data-testid={`${testid}-tobacco`} title={v ? provText(v.prov && v.prov.tobacco) : ''}>
                {m.has.tobacco ? (v ? `${tobaccoLabel(v.tobacco)} (${year})` : `Tobacco ${MASK}`)
                  : tLast ? <span style={{ color: c.sub }} data-testid={`${testid}-tobacco-last`}>tobacco last year ({tLast}): {v && v.last_year && v.last_year.tobacco ? tobaccoLabel(v.last_year.tobacco.value) : MASK}</span>
                    : <span style={{ color: '#b9770e' }}>tobacco {year} missing</span>}
              </span>
            </div>
          </div>
        );
      })}
      {!compact || sum.household ? (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', padding: '3px 0', borderTop: `1px solid ${c.border}`, marginTop: 4 }} data-testid={`${testid}-household`}>
          <span style={{ minWidth: 150 }}>Household {year}</span>
          <span data-testid={`${testid}-income`}>Income {hhCell('income', money)}</span>
          <span data-testid={`${testid}-size`}>Size {hhCell('size', (x) => x)}</span>
        </div>
      ) : null}
      {error && <div role="alert" style={{ fontSize: fs(12), color: '#c0392b', marginTop: 4 }}>{error}</div>}
    </div>
  );
}

// 5.48.0 after a Start enrollment / quote: what was saved to the client's personal info, and — when a person in the form
// couldn't be matched to one household member for sure — "which member is this?" (the values are re-sent from the form).
export function PiCaptureResult({ c, result, source, valuesFor }) {
  const [picks, setPicks] = useState({});
  const [done, setDone] = useState({});
  const [error, setError] = useState(null);
  if (!result || result.error) return result && result.error ? <div style={{ fontSize: fs(12), color: '#b45309' }} data-testid="pi-capture-error">Personal info wasn't saved (an error) — enter it on the client page.</div> : null;
  if (result.pending) return <div style={{ fontSize: fs(12), color: c.sub }} data-testid="pi-capture-pending">Personal info kept with this enrollment — it's saved to the client once HealthSherpa links the application.</div>;
  const n = Object.values(result.saved || {}).reduce((a, b) => a + (b || 0), 0);
  const save = async (a) => {
    setError(null);
    const k = picks[a.index];
    if (!k) return;
    try {
      await healthsherpa.coverage.capturePick(result.person_id, { plan_year: result.plan_year, source,
        members: [{ member_key: k, relationship: a.relationship, ...valuesFor(a.index) }] });
      setDone((d) => ({ ...d, [a.index]: true }));
    } catch (e) { setError(detailOf(e)); }
  };
  return (
    <div style={{ fontSize: fs(12), marginTop: 6 }} data-testid="pi-capture-result">
      {n > 0 && <div style={{ color: '#1e7e34' }}>Saved to the client's personal info: {Object.entries(result.saved).filter(([, v]) => v).map(([k, v]) => `${v} ${k === 'dob' ? 'date(s) of birth' : k}`).join(', ')}.</div>}
      {(result.ambiguous || []).map((a) => (
        <div key={a.index} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }} data-testid="pi-capture-ambiguous">
          {done[a.index] ? <span style={{ color: '#1e7e34' }}>Saved.</span> : <>
            <span>Which household member is {a.name || REL_LABEL[a.relationship]} ({a.fields.join(', ')})?</span>
            <select value={picks[a.index] || ''} onChange={(e) => setPicks({ ...picks, [a.index]: e.target.value })} data-testid="pi-capture-pick" style={inputStyle(c)}>
              <option value="">Pick…</option>
              {a.candidates.map((x) => <option key={x.member_key} value={x.member_key}>{x.label}</option>)}
            </select>
            <button style={btn(c, true)} disabled={!picks[a.index]} onClick={() => save(a)} data-testid="pi-capture-save">Save</button>
          </>}
        </div>
      ))}
      {error && <div role="alert" style={{ color: '#c0392b' }}>{error}</div>}
    </div>
  );
}
